import { Component, OnInit } from '@angular/core';
import { SelectItem } from 'primeng';
import { MessageService } from 'primeng';
import { EventService } from '../../../services/event.service';
import { CustomerService } from '../../../services/customer.service';
import { UserService } from '../../../services/user.service';
import { DashboardCustomerKPIService } from '../../../services/dashboard-customer-kpi.service';
import { OrderTagService } from '../../../services/order-tag.service';
import { Message } from '../../../classes/Message';
import moment from 'moment';

export enum DateRangeOptionEnum {
  Daily = 1,
  Weekly = 2,
  Monthly = 3,
  Quarterly,
}

@Component({
  selector: 'dashboard-customer-kpi-toplevel',
  templateUrl: './dashboard-customer-kpi-toplevel.component.html',
  styleUrls: ['./dashboard-customer-kpi-display.component.scss'],
})
export class DashboardCustomerKPITopLevelComponent implements OnInit {
  constructor(
    protected eventService: EventService,
    private customerService: CustomerService,
    private userService: UserService,
    private dashboardKPIService: DashboardCustomerKPIService,
    private orderTagService: OrderTagService,
    private messageService: MessageService,
  ) {}

  public DateRangeOptionEnum = DateRangeOptionEnum;

  dateRangeOptions: SelectItem[];
  selectedDateRange: DateRangeOptionEnum = DateRangeOptionEnum.Weekly;

  dailyDateRangeOptions: SelectItem[];
  selectedDailyDateRangeOption: number = 14;

  weeklyDateRangeOptions: SelectItem[];
  selectedWeeklyDateRangeOption: number = -1;

  monthlyDateRangeOptions: SelectItem[];
  selectedMonthlyDateRangeOption: number = 1;

  dateRangeValue: number = -1;

  deptOptions: SelectItem[];
  selectedDept: any;

  orderTagOptions: SelectItem[];
  selectedOrderTag: any;

  public selectedTabIndex: any = 0;

  // set to true when service has loaded KPI data
  isLoaded = false;

  kpiStartDate: Date;
  minKPIDate: Date;
  dateRange: any;

  csvDownloading = false;

  async ngOnInit() {
    this.dateRangeOptions = [
      { label: 'Daily', value: DateRangeOptionEnum.Daily },
      { label: 'Weekly', value: DateRangeOptionEnum.Weekly },
      { label: 'Monthly', value: DateRangeOptionEnum.Monthly },
    ];

    this.kpiStartDate = moment().subtract(14, 'weeks').toDate();
    this.minKPIDate = new Date(2018, 1, 1);

    let userOrg = await this.userService.getCurrentUserOrg();
    this.customerService.getDepartments(userOrg.id).then((depts) => {
      this.deptOptions = depts.map((dept) => {
        return { label: dept.name, value: dept.id };
      });

      // insert 'All' as a department option
      this.deptOptions.unshift({ label: 'All Departments', value: null });

      //console.log("Departments: ", this.deptOptions, " - customerId: ", userOrg.id);
    });

    // this is misnamed, it gets all for organisation
    this.orderTagService.getForCustomer(userOrg.id).then((tags) => {
      this.orderTagOptions = tags.map((tag) => {
        return { label: tag.description, value: tag.id };
      });

      // insert 'All' as a tags option
      this.orderTagOptions.unshift({ label: 'All Order Tags', value: null });
    });

    this.dailyDateRangeOptions = [
      { label: '1 Day', value: 1 },
      { label: '7 Days', value: 7 },
      { label: '14 Days', value: 14 },
      { label: '28 Days', value: 28 },
      { label: 'Whole Month', value: -1 },
    ];

    this.weeklyDateRangeOptions = [];
    for (let i = 1; i <= 12; i++) {
      this.weeklyDateRangeOptions.push({ label: `${i} week${i > 1 ? 's' : ''}`, value: i });
    }

    // this is a twelve week period from today's date
    this.weeklyDateRangeOptions.push({ label: `Period to today`, value: -1 });

    this.monthlyDateRangeOptions = [
      { label: 'This Month', value: 1 },
      { label: 'Three months', value: 3 },
      { label: 'Six months', value: 6 },
      { label: 'Twelve months', value: 12 },
    ];

    // raise change event for charts components
    await this.raiseRefreshEvent();
  }

  onDateRangeChanged(event) {
    switch (this.selectedDateRange) {
      case DateRangeOptionEnum.Daily:
        this.dateRangeValue = this.selectedDailyDateRangeOption;
        break;
      case DateRangeOptionEnum.Weekly:
        this.dateRangeValue = this.selectedWeeklyDateRangeOption;
        break;
      case DateRangeOptionEnum.Monthly:
        this.dateRangeValue = this.selectedMonthlyDateRangeOption;
        break;
    }
    //console.log("date range value: ", event.value);

    this.dateRange = this.dashboardKPIService.getDateRange();

    this.raiseRefreshEvent();
  }

  chartTabChanged(event: any) {
    this.selectedTabIndex = event.index;

    // raise the changed event so charts can be rendered
    this.raiseRefreshEvent();
  }

  onDepartmentChanged(event: any) {
    this.raiseRefreshEvent();
  }

  onOrderTagChanged(event: any) {
    this.raiseRefreshEvent();
  }

  async raiseRefreshEvent() {
    //console.log("Selected order tag: ", this.selectedOrderTag);

    // update service data
    await this.dashboardKPIService.loadCustomerKPIData(
      this.kpiStartDate,
      this.selectedDateRange,
      this.dateRangeValue,
      this.selectedDept,
      this.selectedOrderTag,
    );

    this.dateRange = this.dashboardKPIService.getDateRange();

    this.eventService.CustomerKPIRefreshEvent.emit({
      tabIndex: this.selectedTabIndex,
      dateRangeType: this.selectedDateRange,
      dateRangeValue: this.dateRangeValue,
      dept: this.selectedDept,
      orderTag: this.selectedOrderTag,
    });
  }

  exportCSV() {
    this.csvDownloading = true;
    this.dashboardKPIService
      .downloadToCSV()
      .then(() => {
        this.csvDownloading = false;
      })
      .catch((err) => {
        //console.log("Failed export: ", err);

        let errText = 'Could not export the csv.';
        if (err.status == 403) {
          errText = 'You do not have permission to export this data';
        }

        this.csvDownloading = false;
        this.messageService.add(Message.Error(errText));
      });
  }
}
